import { yupResolver } from "@hookform/resolvers/yup";
import FormSelect from "components/Common/Form/FormSelect";
import FormSwitch from "components/Common/Form/FormSwitch";
import FormInput from "components/Common/Form/FormInput";
import {
  useCreateReason,
  useUpdateReason,
  useReasonTypes,
  useOrderTypes,
} from "hooks/reason/useReason";
import React, { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useToasts } from "react-toast-notifications";
import { Button, Col, Form, Row } from "reactstrap";
import * as yup from "yup";
import { formConvertErrorMessage } from "../../helpers/form_helper";

/**
 * @param {object} props
 * @param {object} [props.reason] - Reason data for update mode
 * @param {function} props.onSuccess - Callback on success
 */
const ReasonForm = ({ reason, onSuccess, ...props }) => {
  const { t } = useTranslation(["reason"]);

  // Get reason types from API
  const { data: reasonTypes, refetch: fetchReasonTypes } = useReasonTypes();

  // Get order types from API
  const { data: orderTypes, refetch: fetchOrderTypes } = useOrderTypes();

  // Default values for the form
  const initDefaultValues = {
    reason: reason?.reason || "",
    type: null,
    reason_order_type: null,
    note_required: reason?.note_required === 1 ? true : false,
    change_status: reason?.change_status === 1 ? true : false,
    picture_required: reason?.picture_required === 1 ? true : false,
  };

  useEffect(() => {
    if (reason) {
      setValue(
        "type",
        reasonTypes?.find(item => item.value == reason.type)
      );
      setValue(
        "reason_order_type",
        orderTypes?.find(item => item.value == reason.reason_order_type)
      );
    }
  }, [reason, reasonTypes, orderTypes]);

  // Handle success for API calls
  const onReasonSuccess = data => {
    onSuccess?.(data.data);
  };

  // Handle errors from API
  const { addToast } = useToasts();
  const onErrorReason = error => {
    if (error.from_date === 422 && error.fields?.length > 0) {
      error.fields.forEach(item => {
        let fieldName = item.field;
        switch (item.field) {
          case "common":
            addToast(item.message, {
              appearance: "error",
              autoDismiss: true,
            });
            break;
          default:
            break;
        }
        setError(fieldName, {
          type: "manual",
          message: formConvertErrorMessage(item.message, t),
        });
      });
    }
  };

  // Create and Update mutations
  const { mutate: createReason, isLoading: isLoadingCreate } = useCreateReason(
    onReasonSuccess,
    onErrorReason
  );
  const { mutate: updateReason, isLoading: isLoadingUpdate } = useUpdateReason(
    onReasonSuccess,
    onErrorReason
  );

  // Form validation
  const validator = {
    reason: yup
      .string()
      .required(t("form.validate.required", { title: t("form.reason") }))
      .max(255, t("form.validate.char_max", { max: 255 })),
    type: yup
      .object()
      .nullable()
      .required(t("form.validate.select", { title: t("form.type") })),
    reason_order_type: yup
      .object()
      .nullable()
      .required(
        t("form.validate.select", { title: t("form.reason_order_type") })
      ),
  };

  const reasonValidationSchema = yup.object().shape(validator);

  // Initialize react-hook-form
  const mainForm = useForm({
    resolver: yupResolver(reasonValidationSchema),
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: initDefaultValues,
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    setValue,
    getValues,
    setError,
    watch,
  } = mainForm;

  // Handle form submission
  const handleSubmitForm = () => {
    handleSubmit(() => {
      const data = getValues();

      // Convert form values to API format
      const finalData = {
        reason: data.reason,
        type: data.type?.value,
        reason_order_type: data.reason_order_type?.value,
        note_required: data.note_required ? 1 : 0,
        change_status: data.change_status ? 1 : 0,
        picture_required: data.picture_required ? 1 : 0,
      };

      if (reason) {
        finalData.reasonId = reason.id;
        updateReason(finalData);
      } else {
        createReason(finalData);
      }
    })();
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchReasonTypes();
    fetchOrderTypes();
  }, []);

  return (
    <Form className="p-4" onSubmit={handleSubmitForm}>
      <Row>
        <Col xs={12}>
          <FormInput
            mainForm={mainForm}
            fieldName="reason"
            t={t}
            isRequired
            type="text"
            placeholder={t("placeholder.enter", { title: t("form.reason") })}
          />
        </Col>
        <Col xs={6}>
          <FormSelect
            mainForm={mainForm}
            fieldName="type"
            t={t}
            isRequired
            dataList={reasonTypes || []}
          />
        </Col>
        <Col xs={6}>
          <FormSelect
            mainForm={mainForm}
            fieldName="reason_order_type"
            t={t}
            isRequired
            dataList={orderTypes || []}
          />
        </Col>
        <Col xs={4}>
          <FormSwitch
            mainForm={mainForm}
            fieldName="note_required"
            t={t}
            title={t("form.note_required")}
          />
        </Col>
        <Col xs={4}>
          <FormSwitch
            mainForm={mainForm}
            fieldName="change_status"
            t={t}
            title={t("form.change_status")}
          />
        </Col>
        <Col xs={4}>
          <FormSwitch
            mainForm={mainForm}
            fieldName="picture_required"
            t={t}
            title={t("form.picture_required")}
          />
        </Col>
        <Col className="text-end">
          <Button
            size="xs"
            color="primary"
            disabled={isLoadingCreate || isLoadingUpdate}
            style={{ backgroundColor: "#3C64B1" }}
            onClick={handleSubmitForm}
          >
            {reason ? t("button.update") : t("button.add")}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default ReasonForm;
